import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesElectricityListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesElectricityCreate,
  useCompanyReportsActivitiesElectricityList,
  useCompanyReportsActivitiesElectricityPreviewRetrieve,
  useCompanyReportsActivitiesElectricityRetrieve,
  useCompanyReportsActivitiesElectricityUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesElectricityListParams,
  CompanyReportsActivitiesElectricityPreviewRetrieveParams,
  ElectricityActivitiesList,
  ElectricityActivityRetrieve,
  PaginatedElectricityActivitiesListList,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetElectricityActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesElectricityListParams
) => {
  return useCompanyReportsActivitiesElectricityList<PaginatedElectricityActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useCreateElectricityActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesElectricityCreate<ElectricityActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesElectricityListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useGetElectricityActivity = ({
  electricityActivityUuid,
  reportUuid,
}: {
  electricityActivityUuid?: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesElectricityRetrieve<ElectricityActivityRetrieve>(
    reportUuid,
    electricityActivityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!electricityActivityUuid,
      },
    }
  );
};

export const useUpdateElectricityActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesElectricityUpdate<ElectricityActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesElectricityListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useDeleteElectricityActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesElectricityListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetElectricityPreview = ({
  companyReportUuid,
  params,
  enabled,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesElectricityPreviewRetrieveParams;
  enabled: boolean;
}) => {
  return useCompanyReportsActivitiesElectricityPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
